import { usePostExternalAccounts } from 'api/payments/generated/fragmentPaymentsAPI';
import { useToast } from 'components/ToastManager';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import { Toast } from '@fragment/ui/src/components/Toast';

import { PaymentsModal } from './PaymentsModal';

const UNSAFE_CHARACTERS = ['#', '/', ':'] as const;
const HANDLEBAR_REGEX = /\{\{.*?\}\}/;

const isSafeString = (value: string): boolean => {
  if (UNSAFE_CHARACTERS.some((char) => value.includes(char))) {
    return false;
  }
  if (HANDLEBAR_REGEX.test(value)) {
    return false;
  }
  return true;
};

type FormData = {
  external_id: string;
  name: string;
};

type CreateExternalAccountModalProps = {
  onClose: VoidFunction;
};

export const CreateExternalAccountModal = ({
  onClose,
}: CreateExternalAccountModalProps) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const postExternalAccountMutation = usePostExternalAccounts();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      external_id: '',
      name: '',
    },
  });

  const onSubmit = useCallback(
    async (formData: FormData) => {
      try {
        await postExternalAccountMutation.mutateAsync({
          data: {
            external_id: formData.external_id,
            name: formData.name,
          },
        });

        await queryClient.invalidateQueries({
          queryKey: ['/external-accounts'],
        });

        showToast(
          <Toast
            type="success"
            message="External account created successfully"
          />
        );

        onClose();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to create external account. Please try again.';
        showToast(
          <Toast
            type="error"
            message={`Failed to create external account: ${errorMessage}`}
          />
        );
      }
    },
    [postExternalAccountMutation, queryClient, showToast, onClose]
  );

  const canSubmit = isValid && !postExternalAccountMutation.isPending;

  return (
    <PaymentsModal
      data-testid="create-external-account-modal"
      onClose={onClose}
      width="sm"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
      onSubmit={handleSubmit(onSubmit)}
      header={<div className="text-main">Create External Account</div>}
      body={
        <div className="flex flex-col space-y-f2">
          <ModalField
            hasError={!!errors.external_id}
            label="External ID"
            labelWidth="w-f12"
          >
            <ModalTextInput
              data-testid="external-id-input"
              placeholder="External ID"
              {...register('external_id', {
                required: 'External ID is required',
                validate: (value) =>
                  isSafeString(value) ||
                  "Cannot contain '#', '/', ':', or {{handlebar}} syntax",
              })}
            />
          </ModalField>
          <ModalField hasError={!!errors.name} label="Name" labelWidth="w-f12">
            <ModalTextInput
              data-testid="name-input"
              placeholder="Account Name"
              {...register('name', {
                required: 'Name is required',
                validate: (value) =>
                  value.trim() !== '' || 'Name cannot be empty',
              })}
            />
          </ModalField>
        </div>
      }
      footer={
        <div className="flex flex-row justify-between w-full">
          <FormButtons
            type="secondary"
            cancelButtonLabel="Cancel"
            submitButtonLabel={
              postExternalAccountMutation.isPending ? 'Creating...' : 'Create'
            }
            onCancel={onClose}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
            onSubmit={handleSubmit(onSubmit)}
            disabled={!canSubmit}
          />
        </div>
      }
    />
  );
};
